<template>
  <AdminLayout>
    <template #content>
      <div class="d-flex justify-content-between align-items-start gap-3 mb-3">
        <div>
          <h1 class="h4 mb-1">Modifier le transfert</h1>
          <div v-if="transfer" class="stock-transfer-edit-meta d-flex flex-wrap align-items-center gap-2">
            <span class="badge" :class="statusClass(transfer.status)">
              {{ statusLabel(transfer.status) }}
            </span>
            <span class="stock-transfer-edit-meta-item">
              <span class="text-muted">Référence</span>
              <strong>{{ transfer.reference }}</strong>
            </span>
            <span class="stock-transfer-edit-meta-item">
              <span class="text-muted">Destination</span>
              <strong>{{ destinationShopName }}</strong>
            </span>
          </div>
        </div>

        <div class="d-flex flex-wrap justify-content-end gap-2">
          <button
            type="button"
            class="btn btn-outline-secondary btn-sm btn-icon-label"
            title="Retour à la liste"
            @click="$router.push({ name: 'StockTransfers' })"
          >
            <i class="fa fa-arrow-left"></i>
            <span class="btn-label ms-1">Retour à la liste</span>
          </button>

          <button
            v-if="transfer"
            type="button"
            class="btn btn-outline-primary btn-sm btn-icon-label"
            title="Voir la fiche"
            @click="$router.push({ name: 'StockTransferShow', params: { id: transfer.id } })"
          >
            <i class="fa fa-file-lines"></i>
            <span class="btn-label ms-1">Voir la fiche</span>
          </button>
        </div>
      </div>

      <div v-if="isLoading" class="text-center my-5">
        <i class="fa fa-spinner fa-spin fa-2x mb-2"></i>
        <div class="small text-muted">Chargement du transfert...</div>
      </div>

      <div v-else-if="errorMessage" class="alert alert-danger">
        {{ errorMessage }}
      </div>

      <div v-else-if="!transfer" class="alert alert-warning">
        Transfert introuvable.
      </div>

      <div v-else-if="!canEditTransfer" class="alert alert-warning">
        Seuls les transferts en brouillon du site source peuvent être modifiés.
      </div>

      <div v-else class="card border-0 shadow-sm">
        <div class="card-body">
          <StockTransferForm
            mode="edit"
            :transfer="transfer"
            :submitting="isSubmitting"
            @save="updateTransfer"
            @cancel="$router.push({ name: 'StockTransferShow', params: { id: transfer.id } })"
          />
        </div>
      </div>
    </template>
  </AdminLayout>
</template>

<script>
import StockTransferForm from './StockTransferForm.vue';

export default {
  name: 'StockTransferEditView',
  components: {
    StockTransferForm,
  },
  data() {
    return {
      transfer: null,
      isLoading: false,
      isSubmitting: false,
      errorMessage: '',
    };
  },
  computed: {
    currentShopId() {
      return this.$store.getters['shop/currentShopId'];
    },
    canEditTransfer() {
      return this.transfer?.status === 'draft'
        && Number(this.transfer?.source_shop_id) === Number(this.currentShopId);
    },
    destinationShopName() {
      return this.transfer?.destination_shop?.name || this.transfer?.destinationShop?.name || '-';
    },
  },
  mounted() {
    this.fetchTransfer();
  },
  methods: {
    async fetchTransfer() {
      this.isLoading = true;
      this.errorMessage = '';
      this.transfer = null;

      try {
        const response = await this.$api.get(`/stock-transfers/${this.$route.params.id}`);
        this.transfer = response.data.data;
      } catch (error) {
        this.errorMessage = 'Impossible de charger le transfert.';
      } finally {
        this.isLoading = false;
      }
    },
    async updateTransfer(payload) {
      if (!this.transfer) return;

      this.isSubmitting = true;
      this.errorMessage = '';

      try {
        const response = await this.$api.put(`/stock-transfers/${this.transfer.id}`, payload);
        const transfer = response.data.data;
        this.$router.push({ name: 'StockTransferShow', params: { id: transfer.id } });
      } catch (error) {
        this.errorMessage = this.extractErrorMessage(error, 'Impossible de modifier le transfert.');
      } finally {
        this.isSubmitting = false;
      }
    },
    extractErrorMessage(error, fallback) {
      if (error?.response?.data?.errors) {
        return Object.values(error.response.data.errors).flat().join(' ');
      }

      return fallback;
    },
    statusLabel(status) {
      return {
        draft: 'Brouillon',
        sent: 'Expédié',
        received: 'Réceptionné',
        cancelled: 'Annulé',
      }[status] || status;
    },
    statusClass(status) {
      return {
        draft: 'bg-secondary',
        sent: 'bg-warning text-dark',
        received: 'bg-success',
        cancelled: 'bg-danger',
      }[status] || 'bg-secondary';
    },
  },
};
</script>

<style scoped>
.stock-transfer-edit-meta-item {
  align-items: center;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 0.375rem;
  display: inline-flex;
  gap: 0.5rem;
  min-height: 28px;
  padding: 0.25rem 0.6rem;
}
</style>
